// Shell.jsx — the frame every screen renders inside.
//
// One nav definition drives both layouts: a bottom tab bar on a phone and a
// left rail from 900px up (index.css picks which is visible). Two navs would
// drift; this one cannot.

import React from 'react';
import { api } from '../api';
import { navigate } from '../router';
import { useAuth } from '../AuthProvider';
import { signOutEverywhere, signOutLocal } from '../auth';
import { BUILD_INFO } from '../config';
import { initials } from '../format';
import { Button, Icon, KV, Pill, Sheet } from './primitives';

export const NAV = [
  { key: 'overview', path: '/', label: 'Overview', icon: 'overview' },
  { key: 'orgs', path: '/orgs', label: 'Orgs', icon: 'orgs' },
  { key: 'users', path: '/users', label: 'Users', icon: 'users' },
  { key: 'cartons', path: '/cartons', label: 'Cartons', icon: 'cartons' },
  { key: 'monitor', path: '/monitor', label: 'Monitor', icon: 'monitor' },
  { key: 'engine', path: '/engine', label: 'Engine', icon: 'monitor' },
  { key: 'support', path: '/support', label: 'Support', icon: 'support' },
];

function NavItems({ active, badges }) {
  return NAV.map((item) => (
    <button key={item.key} className="tab" type="button"
            aria-current={active === item.key ? 'page' : undefined}
            onClick={() => navigate(item.path)}>
      <Icon name={item.icon} size={21} />
      <span>{item.label}</span>
      {badges?.[item.key] > 0 && (
        <span className="badge">{badges[item.key] > 99 ? '99+' : badges[item.key]}</span>
      )}
    </button>
  ));
}

function AccountSheet({ onClose, health }) {
  const { user } = useAuth();
  const [busy, setBusy] = React.useState(false);
  return (
    <Sheet title={user?.display_name || user?.email || 'Account'}
           subtitle={user?.email}
           onClose={onClose}
           footer={(
             <>
               <Button onClick={() => { signOutLocal(); onClose(); }}>Sign out</Button>
               <Button variant="danger" loading={busy}
                       onClick={async () => {
                         setBusy(true);
                         await signOutEverywhere();   // revokes the family server-side
                         onClose();
                       }}>
                 Sign out everywhere
               </Button>
             </>
           )}>
      <div className="stack">
        <KV items={[
          ['Role', <span key="r" className="row" style={{ gap: 6 }}>
            <Pill tone="accent">superuser</Pill>
            <span className="mono dim" style={{ fontSize: 11 }}>{user?.role}</span>
          </span>],
          ['Org', user?.org_name || user?.org_id],
          ['API', <span key="a" className="mono" style={{ fontSize: 11.5 }}>{BUILD_INFO.apiBase}</span>],
          ['Build', <span key="b" className="mono" style={{ fontSize: 11.5 }}>{BUILD_INFO.mode}</span>],
          ['Backend', health
            ? <span className="mono" style={{ fontSize: 11.5 }}>
                v{health.app_version} · engine {health.engine_version || '—'}
              </span>
            : null],
        ]} />
        <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
          &ldquo;Sign out everywhere&rdquo; revokes every refresh token for your
          account on every device. Access tokens already issued stay valid for up
          to 15 minutes &mdash; that is the backend&rsquo;s design, not a delay here.
        </div>
      </div>
    </Sheet>
  );
}

/** Backend reachability, polled on a slow loop. `/health` needs no auth, so it
 *  keeps answering when the session is the thing that is broken — which is the
 *  moment you most want to know whether the API itself is up. */
function useHealth() {
  const [state, setState] = React.useState({ data: null, down: false });
  React.useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const data = await api.health();
        if (alive) setState({ data, down: false });
      } catch {
        if (alive) setState((s) => ({ ...s, down: true }));
      }
    };
    tick();
    const t = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  return state;
}

export default function Shell({ active, badges, children }) {
  const { user } = useAuth();
  const [account, setAccount] = React.useState(false);
  const { data: health, down } = useHealth();
  const dbDown = health && health.db && health.db.ok === false;

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <svg className="brand-mark" width="22" height="22" viewBox="0 0 22 22"
               fill="none" aria-hidden>
            <rect x="1.6" y="1.6" width="18.8" height="18.8" rx="3"
                  stroke="var(--ink)" strokeWidth="1.2" />
            <rect x="6" y="6" width="10" height="10" rx="1.5" fill="var(--accent)" />
          </svg>
          <div className="col" style={{ gap: 0 }}>
            <span className="brand-text">Control Center</span>
            <span className="brand-sub">Mono superuser</span>
          </div>
        </div>
        <div className="spacer" />
        {(down || dbDown) && (
          <Pill tone="danger" title={down ? 'The API did not answer' : 'The API is up but its database is not'}>
            <span className="dot" />{down ? 'API down' : 'DB down'}
          </Pill>
        )}
        <button className="avatar" onClick={() => setAccount(true)}
                aria-label="Account" title={user?.email}>
          {initials(user)}
        </button>
      </header>

      <div className="layout">
        <nav className="sidebar" aria-label="Sections">
          <div className="stack" style={{ gap: 3 }}>
            <NavItems active={active} badges={badges} />
          </div>
        </nav>
        <main className="main">{children}</main>
      </div>

      <nav className="tabbar" aria-label="Sections">
        <NavItems active={active} badges={badges} />
      </nav>

      {account && <AccountSheet onClose={() => setAccount(false)} health={health} />}
    </div>
  );
}
